import { useState, useEffect, useCallback } from 'react';
import { useBorneStore } from '@/store/borne-store';
import { useConfigStore } from '@/store/config-store';
import { useHardwareStore } from '@/store/hardware-store';
import { agentClient, type Product } from '@/services/agent-client';
import { productCache } from '@/services/product-cache';
import { TpeSerial } from '@/hardware/tpe-serial';
import { PrinterUsb } from '@/hardware/printer-usb';
import { NumPad } from '@/components/NumPad';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Tab = 'agent' | 'tpe' | 'printer' | 'tickets' | 'products' | 'logs';

const TABS: { id: Tab; label: string }[] = [
  { id: 'agent', label: 'Agent' },
  { id: 'tpe', label: 'TPE' },
  { id: 'printer', label: 'Imprimante' },
  { id: 'tickets', label: 'Tickets' },
  { id: 'products', label: 'Produits' },
  { id: 'logs', label: 'Logs' },
];

// ---------------------------------------------------------------------------
// Status badge helper
// ---------------------------------------------------------------------------

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    connected: 'bg-success',
    disconnected: 'bg-gray-600',
    busy: 'bg-warning',
    error: 'bg-danger',
    online: 'bg-success',
    offline: 'bg-gray-600',
    blocked: 'bg-danger',
  };

  const bg = colors[status] ?? 'bg-gray-600';

  return (
    <span
      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium text-white ${bg}`}
    >
      <span className="w-2 h-2 rounded-full bg-white/80" />
      {status}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Agent tab
// ---------------------------------------------------------------------------

function AgentTab() {
  const borneId = useConfigStore((s) => s.borneId);
  const borneName = useConfigStore((s) => s.borneName);
  const setConfig = useConfigStore((s) => s.setConfig);
  const connectionStatus = useBorneStore((s) => s.connectionStatus);
  const ticketsPrinted = useConfigStore((s) => s.ticketsPrinted);
  const maxTickets = useConfigStore((s) => s.maxTickets);
  const resetTickets = useConfigStore((s) => s.resetTickets);

  const [id, setId] = useState(borneId);
  const [name, setName] = useState(borneName);
  const [testResult, setTestResult] = useState<'idle' | 'ok' | 'fail'>('idle');

  async function handleTest() {
    setTestResult('idle');
    setConfig({ borneId: id, borneName: name });
    const ok = await agentClient.testConnection();
    setTestResult(ok ? 'ok' : 'fail');
  }

  function handleSave() {
    setConfig({ borneId: id, borneName: name });
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <span className="text-gray-400 text-sm">Statut connexion:</span>
        <StatusBadge status={connectionStatus} />
      </div>

      <div className="space-y-1">
        <label className="text-sm text-gray-400">ID de la Borne</label>
        <input
          type="text"
          value={id}
          onChange={(e) => setId(e.target.value)}
          className="w-full bg-surface border border-gray-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-accent"
        />
      </div>

      <div className="space-y-1">
        <label className="text-sm text-gray-400">Nom de la Borne</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full bg-surface border border-gray-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-accent"
        />
      </div>

      {testResult === 'ok' && (
        <p className="text-success text-sm">Connexion OK</p>
      )}
      {testResult === 'fail' && (
        <p className="text-danger text-sm">Echec de connexion</p>
      )}

      <div className="flex gap-3">
        <button type="button" onClick={handleTest} className="btn-primary">
          Tester
        </button>
        <button type="button" onClick={handleSave} className="btn-success">
          Sauvegarder
        </button>
      </div>

      {/* Ticket counter */}
      <div className="border-t border-gray-700 pt-6 mt-6 space-y-3">
        <h3 className="text-white font-semibold">Compteur de tickets</h3>
        <p className="text-gray-400 text-sm">
          Tickets imprimes: <span className="text-white font-mono">{ticketsPrinted}</span> / <span className="text-white font-mono">{maxTickets}</span>
        </p>
        {ticketsPrinted >= maxTickets && (
          <p className="text-danger text-sm font-semibold">Limite atteinte</p>
        )}
        <button
          type="button"
          onClick={resetTickets}
          className="bg-warning text-black font-semibold px-4 py-2 rounded-xl transition-all active:scale-95"
        >
          Reinitialiser compteur
        </button>
      </div>

      {/* PIN change */}
      <PinChangeSection />

      {/* Unlock caisse */}
      <UnlockCaisseSection />
    </div>
  );
}

// ---------------------------------------------------------------------------
// PIN change section (inside Agent tab)
// ---------------------------------------------------------------------------

function PinChangeSection() {
  const showToast = useBorneStore((s) => s.showToast);
  const [editing, setEditing] = useState(false);
  const [newPin, setNewPin] = useState('');

  function handleConfirm() {
    if (newPin.length < 4) return;
    useConfigStore.getState().setConfig({ adminPin: newPin });
    setEditing(false);
    setNewPin('');
    showToast('PIN modifie avec succes', 'success');
  }

  return (
    <div className="border-t border-gray-700 pt-6 mt-6 space-y-3">
      <h3 className="text-white font-semibold">Changer le PIN</h3>
      <p className="text-gray-400 text-sm">
        PIN actuel: <span className="text-white font-mono tracking-widest">****</span>
      </p>

      {!editing ? (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="btn-primary"
        >
          Modifier
        </button>
      ) : (
        <div className="space-y-4">
          <NumPad
            value={newPin}
            onChange={setNewPin}
            onSubmit={handleConfirm}
            maxLength={6}
            masked
            placeholder="Nouveau PIN"
          />
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleConfirm}
              disabled={newPin.length < 4}
              className="btn-success disabled:opacity-40"
            >
              Confirmer
            </button>
            <button
              type="button"
              onClick={() => { setEditing(false); setNewPin(''); }}
              className="btn-primary"
            >
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Unlock caisse section (inside Agent tab)
// ---------------------------------------------------------------------------

function UnlockCaisseSection() {
  const showToast = useBorneStore((s) => s.showToast);
  const [loading, setLoading] = useState(false);

  async function handleUnlock() {
    setLoading(true);
    const ok = await agentClient.executeUnlockSequence();
    setLoading(false);
    if (ok) {
      showToast('Caisse debloquee', 'success');
    } else {
      showToast('Echec du deblocage', 'error');
    }
  }

  return (
    <div className="border-t border-gray-700 pt-6 mt-6 space-y-3">
      <h3 className="text-white font-semibold">Debloquer la caisse</h3>
      <p className="text-gray-400 text-sm">
        Envoie une commande de deblocage a l&apos;agent.
      </p>
      <button
        type="button"
        onClick={handleUnlock}
        disabled={loading}
        className="bg-warning text-black font-semibold px-4 py-2 rounded-xl transition-all active:scale-95 disabled:opacity-40"
      >
        {loading ? 'Deblocage...' : 'Debloquer la caisse'}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TPE tab
// ---------------------------------------------------------------------------

function TpeTab() {
  const tpeStatus = useHardwareStore((s) => s.tpeStatus);
  const tpeError = useHardwareStore((s) => s.tpeError);
  const tpePaperStatus = useHardwareStore((s) => s.tpePaperStatus);
  const setTpeStatus = useHardwareStore((s) => s.setTpeStatus);

  const [log, setLog] = useState<string[]>([]);

  function appendLog(msg: string) {
    setLog((prev) => [...prev, `[${new Date().toLocaleTimeString('fr-FR')}] ${msg}`]);
  }

  async function handleConnect() {
    appendLog('Recherche automatique du TPE...');
    const tpe = new TpeSerial();

    // Try auto-connect to remembered port first
    const autoOk = await tpe.autoConnect();
    if (autoOk) {
      setTpeStatus('connected');
      appendLog('TPE reconnecte automatiquement (port memorise)');
      return;
    }

    appendLog('Aucun port memorise, selection manuelle...');
    const ok = await tpe.connect();
    if (ok) {
      setTpeStatus('connected');
      appendLog('TPE connecte');
    } else {
      setTpeStatus('error', 'Connexion echouee');
      appendLog('Echec connexion TPE');
    }
  }

  async function handleHandshake() {
    appendLog('Handshake ENQ/ACK...');
    const tpe = new TpeSerial();
    if (!tpe.isConnected) {
      const connected = await tpe.connect();
      if (!connected) {
        appendLog('TPE non connecte');
        return;
      }
    }
    const ok = await tpe.handshake();
    appendLog(ok ? 'Handshake OK' : 'Handshake echoue');
  }

  async function handleTestPayment() {
    appendLog('Test paiement 0,01 EUR...');
    const tpe = new TpeSerial();
    if (!tpe.isConnected) {
      const connected = await tpe.connect();
      if (!connected) {
        appendLog('TPE non connecte');
        return;
      }
    }
    setTpeStatus('busy');
    const result = await tpe.pay(1);
    setTpeStatus('connected');
    appendLog(`Resultat: ${result.responseType} (${result.elapsedMs}ms)`);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-gray-400 text-sm">Statut TPE:</span>
        <StatusBadge status={tpeStatus} />
        <span className="text-gray-400 text-sm ml-2">Papier:</span>
        <StatusBadge status={tpePaperStatus === 'OK' ? 'connected' : tpePaperStatus === 'EMPTY' ? 'error' : tpePaperStatus === 'LOW' ? 'busy' : 'disconnected'} />
        <span className="text-xs text-gray-500">({tpePaperStatus})</span>
      </div>

      {tpeError && (
        <p className="text-danger text-sm">{tpeError}</p>
      )}

      <div className="flex gap-3 flex-wrap">
        <button type="button" onClick={handleConnect} className="btn-primary">
          Connecter
        </button>
        <button type="button" onClick={handleHandshake} className="btn-primary">
          Test handshake
        </button>
        <button type="button" onClick={handleTestPayment} className="btn-primary">
          Test paiement 0,01&euro;
        </button>
      </div>

      {/* Log output */}
      {log.length > 0 && (
        <div className="bg-surface rounded-xl p-4 max-h-60 overflow-y-auto font-mono text-xs text-gray-300 space-y-1">
          {log.map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Printer tab
// ---------------------------------------------------------------------------

function PrinterTab() {
  const printerStatus = useHardwareStore((s) => s.printerStatus);
  const printerError = useHardwareStore((s) => s.printerError);
  const setPrinterStatus = useHardwareStore((s) => s.setPrinterStatus);

  const [log, setLog] = useState<string[]>([]);

  function appendLog(msg: string) {
    setLog((prev) => [...prev, `[${new Date().toLocaleTimeString('fr-FR')}] ${msg}`]);
  }

  async function handleConnect() {
    appendLog('Connexion imprimante...');
    const printer = new PrinterUsb();
    const ok = await printer.connect();
    if (ok) {
      setPrinterStatus('connected');
      appendLog('Imprimante connectee');
    } else {
      setPrinterStatus('error', 'Connexion echouee');
      appendLog('Echec connexion imprimante');
    }
  }

  async function handleTestPrint() {
    appendLog('Test impression...');
    const printer = new PrinterUsb();
    if (!printer.isConnected) {
      const connected = await printer.connect();
      if (!connected) {
        appendLog('Imprimante non connectee');
        return;
      }
      setPrinterStatus('connected');
    }
    const ok = await printer.testPrint();
    appendLog(ok ? 'Impression OK' : 'Echec impression');
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <span className="text-gray-400 text-sm">Statut imprimante:</span>
        <StatusBadge status={printerStatus} />
      </div>

      {printerError && (
        <p className="text-danger text-sm">{printerError}</p>
      )}

      <div className="flex gap-3">
        <button type="button" onClick={handleConnect} className="btn-primary">
          Connecter
        </button>
        <button type="button" onClick={handleTestPrint} className="btn-primary">
          Test impression
        </button>
      </div>

      {/* Log output */}
      {log.length > 0 && (
        <div className="bg-surface rounded-xl p-4 max-h-60 overflow-y-auto font-mono text-xs text-gray-300 space-y-1">
          {log.map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tickets tab
// ---------------------------------------------------------------------------

function TicketsTab() {
  return (
    <div className="flex flex-col items-center justify-center h-64 gap-4">
      <svg
        width="64"
        height="64"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15M9 5C9 6.10457 9.89543 7 11 7H13C14.1046 7 15 6.10457 15 5M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5M12 12H15M12 16H15M9 12H9.01M9 16H9.01"
          stroke="#666"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <p className="text-gray-500 text-lg">Historique des transactions</p>
      <p className="text-gray-600 text-sm">Bientot disponible</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Products tab
// ---------------------------------------------------------------------------

function ProductsTab() {
  const [products, setProducts] = useState<Record<string, Product>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await productCache.getProducts(true);
      setProducts(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur chargement produits');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const entries = Object.entries(products);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-white font-semibold">
          Produits ({entries.length})
        </h3>
        <button
          type="button"
          onClick={loadProducts}
          disabled={loading}
          className="btn-primary text-sm disabled:opacity-40"
        >
          {loading ? 'Chargement...' : 'Rafraichir'}
        </button>
      </div>

      {error && (
        <p className="text-danger text-sm">{error}</p>
      )}

      {entries.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-gray-700">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-surface-light text-gray-400 text-left">
                <th className="px-3 py-2">Nom</th>
                <th className="px-3 py-2">Prix</th>
                <th className="px-3 py-2">Dispo borne</th>
                <th className="px-3 py-2">Sequence</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(([key, product]) => (
                <tr key={key} className="border-t border-gray-800 text-gray-300">
                  <td className="px-3 py-2">{product.name}</td>
                  <td className="px-3 py-2 font-mono">{(product.price / 100).toFixed(2)}</td>
                  <td className="px-3 py-2">
                    <span className={product.is_available_borne ? 'text-success' : 'text-gray-600'}>
                      {product.is_available_borne ? 'Oui' : 'Non'}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <span className={product.has_sequence ? 'text-success' : 'text-gray-600'}>
                      {product.has_sequence ? 'Oui' : 'Non'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && entries.length === 0 && !error && (
        <p className="text-gray-500 text-sm">Aucun produit en cache.</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Logs tab
// ---------------------------------------------------------------------------

function LogsTab() {
  const connectionStatus = useBorneStore((s) => s.connectionStatus);
  const retryCount = useBorneStore((s) => s.retryCount);
  const [logs, setLogs] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<string | null>(null);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await agentClient.request<{ logs: string }>('/api/logs?lines=200');
      setLogs(data.logs ?? '');
      setLastRefresh(new Date().toLocaleTimeString('fr-FR'));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur chargement logs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-white font-semibold">Logs Agent</h3>
        <button
          type="button"
          onClick={loadLogs}
          disabled={loading}
          className="btn-primary text-sm disabled:opacity-40"
        >
          {loading ? 'Chargement...' : 'Rafraichir'}
        </button>
      </div>

      {/* Connection info */}
      <div className="flex items-center gap-4 flex-wrap text-sm">
        <span className="text-gray-400">
          Connexion: <StatusBadge status={connectionStatus} />
        </span>
        <span className="text-gray-400">
          Retries: <span className="text-white font-mono">{retryCount}</span>
        </span>
        {lastRefresh && (
          <span className="text-gray-500">
            Dernier rafraichissement: {lastRefresh}
          </span>
        )}
      </div>

      {error && (
        <p className="text-danger text-sm">{error}</p>
      )}

      {/* Logs display */}
      <div className="bg-gray-900 rounded-xl p-4 max-h-[400px] overflow-auto">
        <pre className="font-mono text-xs text-gray-300 whitespace-pre-wrap break-all">
          {logs || (loading ? 'Chargement...' : 'Aucun log disponible.')}
        </pre>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AdminScreen
// ---------------------------------------------------------------------------

export function AdminScreen() {
  const navigate = useBorneStore((s) => s.navigate);
  const [activeTab, setActiveTab] = useState<Tab>('agent');

  return (
    <div className="h-full w-full flex flex-col bg-surface">
      {/* Tab bar */}
      <div className="flex border-b border-gray-800">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-4 text-center font-semibold transition-colors ${
              activeTab === tab.id
                ? 'text-accent border-b-2 border-accent'
                : 'text-gray-500 hover:text-gray-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto p-6">
        {activeTab === 'agent' && <AgentTab />}
        {activeTab === 'tpe' && <TpeTab />}
        {activeTab === 'printer' && <PrinterTab />}
        {activeTab === 'tickets' && <TicketsTab />}
        {activeTab === 'products' && <ProductsTab />}
        {activeTab === 'logs' && <LogsTab />}
      </div>

      {/* Return button */}
      <div className="p-4 border-t border-gray-800">
        <button
          type="button"
          onClick={() => navigate('screensaver')}
          className="w-full bg-surface-light border border-gray-600 text-white font-semibold py-4 rounded-xl transition-all active:scale-95"
        >
          Retour
        </button>
      </div>
    </div>
  );
}
